// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { ChevronLeft, CalendarCheck, Check, X, RotateCcw, LayoutDashboard, Pencil } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { parseLocalDate } from '@/utils/formatters';
import { getRlTeams } from "@/lib/rlTeams";
import { formatTime, formatTimeRange } from "@/lib/dates";
import { RefundRecommendationsPanel } from "@/components/admin/event-schedule-requests/RefundRecommendationsPanel";


const TIERS = [
  { value: "all", label: "All" },
  { value: "standard", label: "Standard" },
  { value: "priority", label: "Priority" },
  { value: "club_priority", label: "Club Priority" },
  { value: "showcase", label: "Showcase" },
];

const TIER_BADGE: Record<string, string> = {
  standard: "bg-gray-100 text-gray-700 border-gray-300",
  priority: "bg-cyan-100 text-cyan-800 border-cyan-300",
  club_priority: "bg-blue-900 text-white border-blue-900",
  showcase: "bg-amber-100 text-amber-800 border-amber-300",
};

const TIER_LABEL: Record<string, string> = {
  standard: "Standard",
  priority: "Priority",
  club_priority: "Club Priority",
  showcase: "Showcase",
};

const REFUND_PILL: Record<string, { label: string; cls: string }> = {
  pending: { label: "Refund pending", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  refunded: { label: "Refunded", cls: "bg-green-100 text-green-800 border-green-300" },
  failed: { label: "Refund failed", cls: "bg-red-100 text-red-800 border-red-300" },
};

const titleCase = (s: string) =>
  (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const money = (n: number) =>
  Number(n || 0) === 0
    ? "Included"
    : `$${Number(n).toFixed(2)}`;

/** Stored 24-hour parameter values render 12-hour; storage is untouched. */
const isTimeKey = (k: string) => /(^|_)time$/.test(k) || k === "time";

function ParamsSummary({ params }: { params: any }) {
  if (!params || typeof params !== "object") return <span className="text-muted-foreground">—</span>;
  const entries = Object.entries(params).filter(([, v]) => v !== null && v !== "" && v !== undefined);
  if (entries.length === 0) return <span className="text-muted-foreground">—</span>;

  // A start/end pair reads better as one window.
  const start = params.start_time ? formatTime(params.start_time) : "";
  const end = params.end_time ? formatTime(params.end_time) : "";
  const window = start && end ? formatTimeRange(params.start_time, params.end_time) : "";

  return (
    <div className="text-xs space-y-0.5">
      {window && (
        <div>
          <span className="text-muted-foreground">Window:</span>{" "}
          <span className="font-medium">{window}</span>
        </div>
      )}
      {entries
        .filter(([k]) => !(window && (k === "start_time" || k === "end_time")))
        .map(([k, v]) => (
          <div key={k}>
            <span className="text-muted-foreground">{titleCase(k)}:</span>{" "}
            <span className="font-medium">
              {typeof v === "object"
                ? JSON.stringify(v)
                : isTimeKey(k)
                  ? formatTime(String(v), String(v))
                  : String(v)}
            </span>
          </div>
        ))}
    </div>
  );
}


export default function EventSchedulingRequestsPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [tab, setTab] = useState<"pending_review" | "approved" | "denied">("pending_review");
  const [tierFilter, setTierFilter] = useState<string>("all");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [denyModalOpen, setDenyModalOpen] = useState(false);
  const [denyTarget, setDenyTarget] = useState<any>(null);
  const [denyReason, setDenyReason] = useState("");
  const [renegotiateModalOpen, setRenegotiateModalOpen] = useState(false);
  const [renegotiateTarget, setRenegotiateTarget] = useState<any>(null);
  const [renegotiateMessage, setRenegotiateMessage] = useState("");

  const { data: event } = useQuery({
    queryKey: ["sr-event", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("events").select("id, name, start_date, end_date").eq("id", eventId).maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["scheduling-requests", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data: constraints, error } = await (supabase as any)
        .from("schedule_constraints")
        .select("*")
        .eq("event_id", eventId)
        .is("deleted_at", null)
        // Actionable requests only: unpaid (pending_payment) and superseded
        // rows never appear in the list, so they must not inflate the counts.
        .in("status", ["pending_review", "approved", "partial", "denied"])
        .order("created_at", { ascending: false });

      if (error) throw error;

      const teamIds = Array.from(new Set((constraints || []).map((c: any) => c.team_id).filter(Boolean).map(String)));
      const templateIds = Array.from(new Set((constraints || []).map((c: any) => c.template_id).filter(Boolean)));

      // RecruitLook owns teams. Resolve names through the same batched
      // rl-teams lookup the Live Sheet uses; if RL is unavailable we fall back
      // to the name captured on the registration rather than showing a dash.
      const [regsRes, tplRes] = await Promise.all([
        teamIds.length
          ? (supabase as any)
              .from("event_registrations_v2")
              .select("team_id, recruitlook_team_id, scheduling_tier, display_name_override")
              .eq("event_id", eventId)
          : Promise.resolve({ data: [] }),
        templateIds.length
          ? (supabase as any).from("constraint_templates").select("id, label").in("id", templateIds)
          : Promise.resolve({ data: [] }),
      ]);

      let rlTeamById: Record<string, any> = {};
      if (teamIds.length) {
        try {
          rlTeamById = await getRlTeams(teamIds);
        } catch (err) {
          console.error("RecruitLook team lookup failed", err);
        }
      }

      const tierMap = new Map<string, string>();
      const fallbackNameMap = new Map<string, string>();
      for (const r of regsRes.data || []) {
        for (const key of [r.recruitlook_team_id, r.team_id]) {
          if (!key) continue;
          if (r.scheduling_tier) tierMap.set(String(key), r.scheduling_tier);
          if (r.display_name_override) fallbackNameMap.set(String(key), r.display_name_override);
        }
      }
      const tplMap = new Map((tplRes.data || []).map((t: any) => [t.id, t.label]));

      return (constraints || []).map((c: any) => {
        const key = String(c.team_id ?? "");
        const rl = rlTeamById[key];
        return {
          ...c,
          team_name:
            rl?.display_name ||
            rl?.name ||
            fallbackNameMap.get(key) ||
            c.team_name_snapshot ||
            "—",
          scheduling_tier: tierMap.get(key) || "standard",
          template_label: tplMap.get(c.template_id) || titleCase(c.constraint_type),
        };
      });

    },
  });

  const counts = useMemo(() => {
    const c = { pending_review: 0, approved: 0, denied: 0 };
    rows.forEach((r: any) => {
      if (r.status in c) (c as any)[r.status]++;
    });
    return c;
  }, [rows]);

  const initialTab = useMemo(() => {
    const max = Math.max(counts.pending_review, counts.approved, counts.denied);
    if (max === 0) return 'pending_review';
    if (counts.pending_review === max) return 'pending_review';
    if (counts.approved === max) return 'approved';
    return 'denied';
  }, [counts]);

  const tabSetRef = useRef(false);
  useEffect(() => {
    if (tabSetRef.current) return;
    if (initialTab === 'pending_review') {
      tabSetRef.current = true;
      return;
    }
    setTab(initialTab);
    tabSetRef.current = true;
  }, [initialTab]);

  const totalRevenue = useMemo(
    () =>
      rows
        .filter((r: any) => r.status !== "denied")
        .reduce((s: number, r: any) => s + Number(r.fee_charged || 0), 0),
    [rows],
  );

  const filtered = useMemo(
    () =>
      rows.filter(
        (r: any) =>
          r.status === tab &&
          (tierFilter === "all" || r.scheduling_tier === tierFilter),
      ),
    [rows, tab, tierFilter],
  );

  const refundTierIfApplicable = async (constraintId: string) => {
    try {
      const { data, error } = await invokeEdgeFunction("refund-tier-fee", {
        body: { constraint_id: constraintId },
      });
      if (error) {
        toast.error(`Tier fee refund failed: ${error.message}`);
        return;
      }
      if (data?.skipped) return;
      if (data?.ok) {
        toast.success(
          `Tier fee of $${Number(data.refunded_amount || 0).toFixed(2)} refunded to ${data.team_name || "team"} — all of their scheduling requests were denied.`,
        );
      } else if (data?.error) {
        toast.error(`Tier fee refund failed: ${data.error}`);
      }
    } catch (e: any) {
      toast.error(`Tier fee refund failed: ${e.message}`);
    }
  };

  const handleApprove = async (row: any) => {
    setBusyId(row.id);
    try {
      const { error } = await (supabase as any)
        .from("schedule_constraints")
        .update({ status: "approved", reviewed_by: user?.id, reviewed_at: new Date().toISOString() })
        .eq("id", row.id);
      if (error) throw error;
      await refundTierIfApplicable(row.id);
      toast.success("Request approved");
      queryClient.invalidateQueries({ queryKey: ["scheduling-requests", eventId] });
    } catch (e: any) {
      toast.error(`Failed to approve: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const openDenyModal = (row: any) => {
    setDenyTarget(row);
    setDenyReason("");
    setDenyModalOpen(true);
  };

  const handleDeny = async (row: any, responseNotes: string) => {
    setBusyId(row.id);
    try {
      const fee = Number(row.fee_charged || 0);
      const update: any = {
        status: "denied",
        response_notes: responseNotes.trim(),
        reviewed_by: user?.id,
        reviewed_at: new Date().toISOString(),
      };
      if (fee > 0) update.refund_status = "pending";
      const { error } = await (supabase as any)
        .from("schedule_constraints")
        .update(update)
        .eq("id", row.id);
      if (error) throw error;

      if (fee > 0) {
        const { data, error: rfErr } = await invokeEdgeFunction("refund-constraint-fee", {
          body: { constraint_id: row.id },
        });
        if (rfErr || data?.error) {
          toast.error(`Request fee refund failed: ${rfErr?.message || data?.error}`);
        }
      }
      await refundTierIfApplicable(row.id);
      toast.success("Request denied");
      queryClient.invalidateQueries({ queryKey: ["scheduling-requests", eventId] });
    } catch (e: any) {
      toast.error(`Failed to deny: ${e.message}`);
    } finally {
      setBusyId(null);
      setDenyModalOpen(false);
      setDenyTarget(null);
      setDenyReason("");
    }
  };

  const openRenegotiateModal = (row: any) => {
    setRenegotiateTarget(row);
    setRenegotiateMessage("");
    setRenegotiateModalOpen(true);
  };

  const handleRenegotiate = async (row: any, message: string) => {
    setBusyId(row.id);
    try {
      const { data, error } = await invokeEdgeFunction("propose-renegotiation", {
        body: { constraint_id: row.id, message: message.trim() },
      });
      if (error) throw error;
      if (data?.ok) {
        if (data?.email_sent === false) {
          toast.warning("Proposal saved, but no coach email was on file — follow up manually.");
        } else {
          toast.success("Proposal sent to the coach.");
        }
        setRenegotiateModalOpen(false);
        setRenegotiateTarget(null);
        setRenegotiateMessage("");
        queryClient.invalidateQueries({ queryKey: ["scheduling-requests", eventId] });
      } else if (data?.error) {
        toast.error(`Proposal failed: ${data.error}`);
      }
    } catch (e: any) {
      toast.error(`Failed to send proposal: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const handleRetryRefund = async (row: any) => {
    setBusyId(row.id);
    try {
      const { data, error } = await invokeEdgeFunction("refund-constraint-fee", {
        body: { constraint_id: row.id },
      });
      if (error || data?.error) {
        toast.error(`Retry failed: ${error?.message || data?.error}`);
      } else {
        toast.success("Refund processed");
      }
      queryClient.invalidateQueries({ queryKey: ["scheduling-requests", eventId] });
    } catch (e: any) {
      toast.error(`Retry failed: ${e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const eventDates =
    event?.start_date && event?.end_date
      ? `${format(parseLocalDate(event.start_date), "MMM d")} – ${format(parseLocalDate(event.end_date), "MMM d, yyyy")}`
      : "";

  return (
    <div className="space-y-6 px-4 pt-6 md:px-6 md:pt-10 lg:px-8">
      <div>
        <div className="flex items-center gap-4 mb-3">
          <button
            onClick={() => navigate(`/admin-dashboard/events/${eventId}`)}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to event overview
          </button>
          <button
            onClick={() => navigate(`/admin-dashboard/events/${eventId}/edit`)}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <Pencil className="w-4 h-4" />
            Edit event
          </button>
          <button
            onClick={() => navigate(`/admin-dashboard/events/${eventId}/schedule-draft`)}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <LayoutDashboard className="w-4 h-4" />
            Schedule draft
          </button>
        </div>
        <h1 className="text-2xl font-bold font-heading flex items-center gap-2">
          <CalendarCheck className="w-6 h-6" />
          Scheduling Requests
        </h1>
        <p className="text-muted-foreground">
          {event?.name} {eventDates && <span>· {eventDates}</span>}
        </p>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Review the scheduling constraints teams submitted at registration. Approve to honor them in the
          scheduler; deny if they can't be accommodated.
        </p>
      </div>

      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-6">
          <div>
            <div className="text-xs text-muted-foreground">Total requests</div>
            <div className="text-2xl font-bold">{rows.length}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Request-fee revenue</div>
            <div className="text-2xl font-bold">${totalRevenue.toFixed(2)}</div>
          </div>
        </CardContent>
      </Card>

      <RefundRecommendationsPanel
        eventId={eventId!}
        teamNameLookup={(teamId) =>
          rows.find((r: any) => String(r.team_id ?? "") === String(teamId))?.team_name
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Inbox</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
            <TabsList>
              <TabsTrigger value="pending_review">
                Pending Review ({counts.pending_review})
              </TabsTrigger>
              <TabsTrigger value="approved">Approved ({counts.approved})</TabsTrigger>
              <TabsTrigger value="denied">Denied ({counts.denied})</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="flex flex-wrap gap-2">
            {TIERS.map((t) => (
              <button
                key={t.value}
                onClick={() => setTierFilter(t.value)}
                className={`px-3 py-1 rounded-full text-xs border transition ${
                  tierFilter === t.value
                    ? "bg-foreground text-background border-foreground"
                    : "bg-background text-foreground border-border hover:bg-muted"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Team</TableHead>
                <TableHead>Request</TableHead>
                <TableHead>Details</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead>Fee</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    No requests in this view.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((r: any) => {
                  const tier = r.scheduling_tier || "standard";
                  const refund = r.refund_status && REFUND_PILL[r.refund_status];
                  return (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">{r.team_name}</TableCell>
                      <TableCell>{r.template_label}</TableCell>
                      <TableCell><ParamsSummary params={r.parameters} /></TableCell>
                      <TableCell>
                        <Badge variant="outline" className={TIER_BADGE[tier]}>
                          {TIER_LABEL[tier] || tier}
                        </Badge>
                      </TableCell>
                      <TableCell>{money(r.fee_charged)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {r.created_at ? format(new Date(r.created_at), "MMM d, yyyy") : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {tab === "pending_review" ? (
                          r.renegotiation_status === "proposed" ? (
                            <div className="flex flex-col items-end gap-1">
                              <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                                Renegotiation sent
                              </Badge>
                              <span className="text-xs text-muted-foreground">Awaiting coach response</span>
                            </div>
                          ) : (
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="default"
                                disabled={busyId === r.id}
                                onClick={() => handleApprove(r)}
                              >
                                <Check className="w-4 h-4 mr-1" /> Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                disabled={busyId === r.id}
                                onClick={() => openRenegotiateModal(r)}
                              >
                                Renegotiate
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={busyId === r.id}
                                onClick={() => openDenyModal(r)}
                              >
                                <X className="w-4 h-4 mr-1" /> Deny
                              </Button>
                            </div>
                          )
                        ) : tab === "denied" ? (
                          <div className="flex justify-end items-center gap-2">
                            {refund && (
                              <Badge variant="outline" className={refund.cls}>
                                {refund.label}
                              </Badge>
                            )}
                            {(r.refund_status === "pending" || r.refund_status === "failed") && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={busyId === r.id}
                                onClick={() => handleRetryRefund(r)}
                              >
                                <RotateCcw className="w-4 h-4 mr-1" /> Retry refund
                              </Button>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AlertDialog open={denyModalOpen} onOpenChange={setDenyModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deny Scheduling Request</AlertDialogTitle>
            <AlertDialogDescription>
              Provide a reason for the denial. This will be shared with the coach.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <label htmlFor="deny-reason" className="text-sm font-medium">
              Reason for denial
            </label>
            <Textarea
              id="deny-reason"
              value={denyReason}
              onChange={(e) => setDenyReason(e.target.value)}
              placeholder="Enter reason..."
              className="min-h-[100px]"
            />
            <p className="text-xs text-muted-foreground">Shared with the coach</p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDenyModalOpen(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!denyReason.trim()}
              onClick={() => {
                if (denyTarget && denyReason.trim()) {
                  handleDeny(denyTarget, denyReason.trim());
                }
              }}
            >
              Confirm Denial
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={renegotiateModalOpen} onOpenChange={setRenegotiateModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Propose an alternative</AlertDialogTitle>
            <AlertDialogDescription>
              Review the original request below, then explain what you can offer instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4">
            {renegotiateTarget && (
              <div className="rounded-md border bg-muted/40 p-3 space-y-1">
                <div className="text-sm font-medium">{renegotiateTarget.team_name}</div>
                <div className="text-sm">{renegotiateTarget.template_label}</div>
                <ParamsSummary params={renegotiateTarget.parameters} />
              </div>
            )}
            <div className="space-y-2">
              <label htmlFor="renegotiate-message" className="text-sm font-medium">
                Your proposal to the coach
              </label>
              <Textarea
                id="renegotiate-message"
                value={renegotiateMessage}
                onChange={(e) => setRenegotiateMessage(e.target.value)}
                placeholder="We can't hold 10–11am open, but we can guarantee no games before 11:30am…"
                className="min-h-[100px]"
              />
              <p className="text-xs text-muted-foreground">
                Explain what you can offer instead. The coach will accept or decline.
              </p>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRenegotiateModalOpen(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!renegotiateMessage.trim() || busyId === renegotiateTarget?.id}
              onClick={() => {
                if (renegotiateTarget && renegotiateMessage.trim()) {
                  handleRenegotiate(renegotiateTarget, renegotiateMessage.trim());
                }
              }}
            >
              Send Proposal
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
